import {ChangeDetectionStrategy, Component, computed, signal} from '@angular/core';
import {BulkUploadCommercialReleases} from "@portbase/bezoekschip-service-typescriptmodels";
import {map} from "rxjs/operators";
import {AppContext} from "../../../app-context";
import {sendCommand} from "../../../common/utils";
import {parseExcel} from "../../../common/upload/excel.utils";
import {
  commercialReleaseTemplate,
  createDeclarations
} from "../../../commercial-release/commercial-release-upload.utils";

const ALLOWED_EXTENSIONS = ['.xlsx', '.xls'];

/**
 * Popup (NOT sliding-panel) modal for the drag-and-drop Excel upload flow on the new commercial-release
 * overview - see `.kiro/agents/tasks/commercial-release-upload-modal-redesign.md` Decision 1. Instantiated
 * dynamically by `ConfirmationModalComponent` (`common/confirmation-modal/confirmation-modal.component.ts`)
 * via `componentRef.instance['data'] = ...` / `componentRef.instance['callback'] = ...` plain property
 * assignment - the exact same contract `ModalConfirmAutofocus` already uses. That assignment mechanism is
 * why `data`/`callback` below are plain fields rather than signal `input()`s: a signal input is a read-only
 * function, not a settable property, and can't be assigned to this way (it would require
 * `componentRef.setInput(...)`, which this dynamic-component call site does not use). All other, genuinely
 * internal state uses signals + `OnPush`, per the new-leaf-component policy.
 */
@Component({
  selector: 'app-commercial-release-upload-modal',
  templateUrl: './commercial-release-upload-modal.component.html',
  styleUrls: ['./commercial-release-upload-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CommercialReleaseUploadModalComponent {
  data: any;
  callback: (confirmed: boolean) => any;

  protected readonly templateUrl = '/assets/templates/commercial-release-upload-v1.1.xlsx';

  protected readonly selectedFile = signal<File | null>(null);
  protected readonly dragOver = signal<boolean>(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly parsedCommand = signal<BulkUploadCommercialReleases | null>(null);
  protected readonly sending = signal<boolean>(false);

  // "Add to table" is only enabled once a file was selected AND parsed without error (Decision 3).
  protected readonly canAddToTable = computed(() =>
    !!this.selectedFile() && !!this.parsedCommand() && !this.errorMessage() && !this.sending());

  // Drag-over-child flicker guard: `dragenter`/`dragleave` fire on every child element boundary
  // crossing too, so a naive dragOver=true/false toggle on those two events alone flickers the hover
  // style whenever the pointer crosses a child of the dropzone. A counter (incremented on `dragenter`,
  // decremented on `dragleave`) only reaches zero once the pointer has actually left the dropzone and
  // all of its descendants - there is no existing precedent for this in the codebase (confirmed via
  // search; only unrelated CDK `cdkDragDrop` list-reordering exists), so this is a new, self-contained
  // counter rather than a copied pattern.
  private dragDepth = 0;

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth++;
    this.dragOver.set(true);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.dragOver.set(false);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = 0;
    this.dragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFile(file);
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.handleFile(file);
    }
    input.value = '';
  }

  // Single funnel point for both the drop handler and the click-to-browse `<input>` - no duplicated
  // extension-check/parse logic between the two entry points (task Decision/Scope requirement).
  handleFile(file: File): void {
    this.selectedFile.set(file);
    this.parsedCommand.set(null);
    this.errorMessage.set(null);

    if (!this.hasAllowedExtension(file.name)) {
      this.errorMessage.set('File invalid, extension must be one of: ' + ALLOWED_EXTENSIONS.join(", "));
      return;
    }

    parseExcel(file, commercialReleaseTemplate)
      .pipe(map(data => <BulkUploadCommercialReleases>{releases: createDeclarations(data.releases)}))
      .subscribe({
        next: command => {
          if (command.releases.length === 0) {
            this.errorMessage.set('File invalid, no content found in the file');
            return;
          }
          this.parsedCommand.set(command);
        },
        error: () => this.errorMessage.set('File invalid')
      });
  }

  private hasAllowedExtension(fileName: string): boolean {
    const lower = fileName.toLowerCase();
    return ALLOWED_EXTENSIONS.some(ext => lower.endsWith(ext));
  }

  // Lets the user clear a selected/dropped file before "Add to table", per the Figma's selected-file
  // state (filename + "Remove" affordance) - without this, swapping files requires dragging a new one
  // over the dropzone rather than explicitly clearing the current selection first.
  removeFile(event: Event): void {
    event.stopPropagation();
    this.selectedFile.set(null);
    this.parsedCommand.set(null);
    this.errorMessage.set(null);
  }

  addToTable(): void {
    const command = this.parsedCommand();
    if (!command) {
      return;
    }
    this.sending.set(true);
    sendCommand("com.portbase.bezoekschip.common.api.commercialrelease.BulkUploadCommercialReleases",
      command,
      () => {
        this.sending.set(false);
        this.callback(true);
      },
      error => {
        this.sending.set(false);
        AppContext.registerError(error);
      });
  }

  cancel(): void {
    this.callback(false);
  }
}
